"use client";

import * as React from "react";
import { Phone, Mail, MapPin, MessageCircle, LoaderCircle, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/components/common/Link";
const StartProjectForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 bg-card/50 backdrop-blur-sm rounded-xl border border-border animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/20 text-primary rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-2">Request Received!</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage || "Thank you for your project details. We'll review your request and get back to you with an estimate shortly."}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:text-primary">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-background/50 border-input/50 focus-visible:ring-primary focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-background/50 border-input/50 focus-visible:ring-primary focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} className="bg-background/50 border-input/50 focus-visible:ring-primary focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_type" rules={{
        required: "Please select a project type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90">Type of Project</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/50 border-input/50 focus:ring-primary focus:border-primary">
                    <SelectValue placeholder="Select a project type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Whole-house remodel">Whole-house remodel</SelectItem>
                  <SelectItem value="Handyman work">Handyman work</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="address" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Project Address <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <Input placeholder="123 Main St, City, State" {...field} className="bg-background/50 border-input/50 focus-visible:ring-primary focus-visible:border-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget" rules={{
      min: {
        value: 0,
        message: "Budget cannot be negative"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Estimated Budget (USD) <span className="text-muted-foreground text-xs font-normal ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <Input type="number" placeholder="e.g., 15000" {...field} className="bg-background/50 border-input/50 focus-visible:ring-primary focus-visible:border-primary" />
            </FormControl>
            <FormDescription className="text-muted-foreground/70">
              Optional – helps us tailor the estimate
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" rules={{
      required: "Please describe your project details"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90">Project Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe the work you need, any specific requirements, timelines, etc." className="min-h-[120px] bg-background/50 border-input/50 focus-visible:ring-primary focus-visible:border-primary resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 transition-all duration-300">
        {isSubmitting ? <>
            <LoaderCircle className="mr-2 size-5 animate-spin" />
            Submitting Estimate...
          </> : "Submit Estimate"}
      </Button>
    </Form>;
};
export default function StartYourProject() {
  return <section id="start-your-project" className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      {}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-background pointer-events-none" />
      
      {}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-primary/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="container relative mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className="flex flex-col justify-center h-full space-y-10 lg:pr-8">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white">
                Let's Build Something <span className="text-primary">Great Together</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                Ready to transform your home? Whether it's a full remodel or handyman work, we're here to help. Contact us directly or fill out the form for a free estimate.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 size-14 rounded-2xl bg-card border border-border flex items-center justify-center text-primary shadow-sm group-hover:border-primary/50 group-hover:shadow-primary/10 transition-all duration-300">
                  <Phone className="size-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">Call Us Directly</h3>
                  
                  <Link to="tel:5551234567" className="text-xl font-bold text-white hover:text-primary transition-colors">920.325.6665</Link>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 size-14 rounded-2xl bg-card border border-border flex items-center justify-center text-primary shadow-sm group-hover:border-primary/50 group-hover:shadow-primary/10 transition-all duration-300">
                  <Mail className="size-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">Email Us</h3>
                  
                  <Link to="mailto:info@callgreenhaven.com" className="text-xl font-bold text-white hover:text-primary transition-colors">GreenHavenProperty.Online@GMail.com</Link>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 size-14 rounded-2xl bg-card border border-border flex items-center justify-center text-primary shadow-sm group-hover:border-primary/50 group-hover:shadow-primary/10 transition-all duration-300">
                  <MapPin className="size-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">We Can Work within a 50 Mile Radius of Green Bay</h3>
                  <p className="text-muted-foreground text-sm mb-1">Will Travel Further When Necessary</p>
                  
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button size="lg" className="h-14 px-8 text-base gap-3 bg-white text-background hover:bg-white/90 font-bold shadow-xl shadow-white/5">
                <MessageCircle className="size-5" />
                Chat Now
              </Button>
            </div>
          </div>

          {}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-b from-primary/30 to-transparent rounded-2xl blur-sm opacity-50 pointer-events-none" />
            <div className="relative bg-card/30 backdrop-blur-md border border-white/10 rounded-2xl p-6 md:p-8 lg:p-10 shadow-2xl">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">Start Your Project</h3>
                <p className="text-muted-foreground">
                  Fill out the form below to request a bid or estimate. We'll get back to you within 24 hours.
                </p>
              </div>

              <FormProvider formId="start_project_form_home" sectionName="start-your-project" defaultValues={{
              name: "",
              email: "",
              phone: "",
              address: "",
              project_type: "",
              budget: "",
              details: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              project_type: {
                required: true
              },
              details: {
                required: true
              }
            }} submitText="Submit Estimate" successMessage="Thank you! Your estimate request has been received. We will contact you shortly.">
                <StartProjectForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}